import { QuoteStatus } from '../types';

/**
 * Formata um valor em minutos para uma string humanizada (Dias, Horas, Minutos).
 * Ex: 480 -> "8h", 1440 -> "1 dia", 90 -> "1h 30m"
 */
export const formatMinutes = (minutes: number): string => {
  if (!isFinite(minutes) || minutes === null || minutes === undefined) return '0 min';
  if (minutes <= 0) return '0 min';
  if (minutes < 60) return `${minutes} min`;
  
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  
  if (hours >= 24) {
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    
    let result = `${days} dia${days > 1 ? 's' : ''}`;
    if (remainingHours > 0) result += ` e ${remainingHours}h`;
    return result;
  }
  
  let result = `${hours}h`;
  if (remainingMinutes > 0) result += ` ${remainingMinutes}m`;
  return result;
};

/**
 * Formata um número para o padrão de moeda BRL (R$ 0,00).
 */
export const formatCurrencyBRL = (value: number): string => {
  if (!isFinite(value) || value === null || value === undefined) return 'R$ 0,00';
  const formatted = new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
  return formatted.replace(/^R\$\s?R\$/, 'R$');
};

/**
 * Máscara de moeda reversa (estilo bancário).
 * Recebe uma string de dígitos e retorna "R$ 0,00" formatado.
 */
export const formatCurrencyMask = (value: string): string => {
  const digits = value.replace(/\D/g, '');
  const amount = parseInt(digits || '0', 10) / 100;
  return formatCurrencyBRL(amount);
};

/**
 * Converte a string da máscara de volta para número (float).
 */
export const parseCurrencyMask = (value: string): number => {
  const digits = value.replace(/\D/g, '');
  return parseInt(digits || '0', 10) / 100;
};

/**
 * Traduz o status técnico (inglês) para o rótulo exibido ao usuário (português).
 */
export const translateQuoteStatus = (status: QuoteStatus | string): string => {
  switch (status) {
    case 'draft': return 'Rascunho';
    case 'sent': return 'Pendente';
    case 'approved': return 'Fechado';
    case 'rejected': return 'Recusado';
    default: return status;
  }
};

/**
 * Converte expressões flexíveis de tempo em minutos inteiros.
 * Exemplos:
 * - "1 hora e meia" -> 90
 * - "meia hora" -> 30
 * - "1,5h" / "1.5" -> 90
 * - "0.5h" -> 30
 * - "1h 30m" / "1h30" / "1:30" -> 90
 * - "45 min" / "45m" -> 45
 * - 90 -> 90
 */
export const parseFlexibleTimeToMinutes = (input: string | number): number => {
  if (typeof input === 'number') return Math.max(0, Math.round(input));
  if (!input || typeof input !== 'string') return 0;

  const raw = input.trim().toLowerCase().replace(',', '.');
  if (!raw) return 0;

  // 1. Caso "meia hora" ou "X hora(s) e meia"
  if (raw.includes('meia')) {
    const hoursMatch = raw.match(/(\d+(?:\.\d+)?)\s*(?:h|hora)/);
    const hours = hoursMatch ? parseFloat(hoursMatch[1]) : 0;
    if (hours > 0) return Math.round(hours * 60 + 30);
    return 30; // ex: "meia hora"
  }

  // 2. Formato HH:MM (ex: "1:30" -> 90 min, "0:45" -> 45 min)
  const timeFormatMatch = raw.match(/^(\d+):(\d{1,2})$/);
  if (timeFormatMatch) {
    const h = parseInt(timeFormatMatch[1], 10);
    const m = parseInt(timeFormatMatch[2], 10);
    return h * 60 + m;
  }

  // 3. Formato com 'h' e/ou 'm' (ex: "1h 30m", "1h30", "2h", "45m")
  const hMatch = raw.match(/(\d+(?:\.\d+)?)\s*(?:h|hora|horas)/);
  const mMatch = raw.match(/(\d+)\s*(?:m|min|minuto|minutos)/);

  if (hMatch || mMatch) {
    const hours = hMatch ? parseFloat(hMatch[1]) : 0;
    const mins = mMatch ? parseInt(mMatch[1], 10) : 0;
    return Math.round(hours * 60 + mins);
  }

  // 4. Se o usuário digitou apenas um decimal como "1.5" ou "0.5" (assumimos horas)
  if (/^\d+\.\d+$/.test(raw)) {
    return Math.round(parseFloat(raw) * 60);
  }

  // 5. Numeral puro (ex: "90" -> 90 minutos)
  const pureNum = parseFloat(raw.replace(/[^\d.]/g, ''));
  return isNaN(pureNum) ? 0 : Math.round(pureNum);
};

/**
 * Mascara e sanitiza entradas de telefone brasileiro.
 * Trata entradas automáticas/autofill de celulares e navegadores móveis (+55 DDD NÚMERO).
 */
export const maskPhone = (val?: string): string => {
  if (!val) return '';
  let v = val.replace(/\D/g, '');

  // Remove zero à esquerda do DDD (ex: 011999998888 -> 11999998888)
  if (v.startsWith('0') && (v.length === 11 || v.length === 12)) {
    v = v.slice(1);
  }

  // Remove código DDI 55 (Brasil) quando vindo de autofill móvel / WhatsApp (+55 19 98339-3333 -> 13 dígitos)
  if (v.startsWith('55') && v.length > 11) {
    v = v.slice(2);
  }

  if (v.length > 11) {
    v = v.slice(0, 11);
  }

  if (v.length > 6) {
    return `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7)}`;
  }
  if (v.length > 2) {
    return `(${v.slice(0, 2)}) ${v.slice(2)}`;
  }
  return v;
};

/**
 * Formata qualquer número ou string de telefone para exibição limpa e legível.
 * Resolve números salvos com DDI 55 ou formatos brutos de banco de dados.
 */
export const formatPhone = (phoneStr?: string): string => {
  if (!phoneStr) return '';
  let digits = String(phoneStr).replace(/\D/g, '');

  if (digits.startsWith('0') && (digits.length === 11 || digits.length === 12)) {
    digits = digits.slice(1);
  }

  if (digits.startsWith('55') && (digits.length === 12 || digits.length === 13)) {
    digits = digits.slice(2);
  }

  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return String(phoneStr);
};

/**
 * Converte um número inteiro menor que 1000 em sua representação por extenso.
 */
function convertGroupToWords(num: number): string {
  if (num === 0) return '';
  if (num === 100) return 'cem';

  const units = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
  const teens = ['dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
  const tens = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
  const hundreds = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];

  const parts: string[] = [];

  const h = Math.floor(num / 100);
  const remainder = num % 100;
  const t = Math.floor(remainder / 10);
  const u = remainder % 10;

  if (h > 0) {
    parts.push(hundreds[h]);
  }

  if (remainder >= 10 && remainder <= 19) {
    parts.push(teens[remainder - 10]);
  } else {
    if (t > 0) parts.push(tens[t]);
    if (u > 0) parts.push(units[u]);
  }

  return parts.join(' e ');
}

/**
 * Converte um valor numérico em reais (BRL) para sua representação textual por extenso.
 * Exemplos:
 * - 0.05 -> "Cinco centavos"
 * - 1.00 -> "Um real"
 * - 1.01 -> "Um real e um centavo"
 * - 150.00 -> "Cento e cinquenta reais"
 * - 1250.75 -> "Um mil, duzentos e cinquenta reais e setenta e cinco centavos"
 */
export const formatCurrencyExtenso = (value: number): string => {
  if (!isFinite(value) || isNaN(value)) return 'Zero real';

  const rounded = Math.round(Math.abs(value) * 100) / 100;
  const integerPart = Math.floor(rounded);
  const centsPart = Math.round((rounded - integerPart) * 100);

  if (integerPart === 0 && centsPart === 0) {
    return 'Zero real';
  }

  const scales = [
    { singular: '', plural: '' },
    { singular: 'mil', plural: 'mil' },
    { singular: 'milhão', plural: 'milhões' },
    { singular: 'bilhão', plural: 'bilhões' }
  ];

  const wordsParts: string[] = [];

  if (integerPart > 0) {
    let temp = integerPart;
    const groups: number[] = [];

    while (temp > 0) {
      groups.push(temp % 1000);
      temp = Math.floor(temp / 1000);
    }

    const groupWords: string[] = [];

    for (let i = groups.length - 1; i >= 0; i--) {
      const g = groups[i];
      if (g === 0) continue;

      const gWord = convertGroupToWords(g);
      const scale = scales[i];
      const scaleName = g > 1 ? scale.plural : scale.singular;

      if (scaleName) {
        groupWords.push(`${gWord} ${scaleName}`.trim());
      } else {
        groupWords.push(gWord);
      }
    }

    let realText = '';
    if (groupWords.length === 1) {
      realText = groupWords[0];
    } else if (groupWords.length === 2) {
      realText = `${groupWords[0]} e ${groupWords[1]}`;
    } else {
      const rest = groupWords.slice(0, -1).join(', ');
      const last = groupWords[groupWords.length - 1];
      realText = `${rest} e ${last}`;
    }

    // Se termina exatamente em milhão ou bilhão sem nada menor (ex: 2.000.000)
    const isExactMillionOrBillion = groups.length > 2 && groups.slice(0, -1).every(v => v === 0);
    const currencySuffix = isExactMillionOrBillion
      ? ' de reais'
      : (integerPart === 1 ? ' real' : ' reais');

    wordsParts.push(`${realText}${currencySuffix}`);
  }

  if (centsPart > 0) {
    const centsWords = convertGroupToWords(centsPart);
    const centsSuffix = centsPart === 1 ? ' centavo' : ' centavos';
    wordsParts.push(`${centsWords}${centsSuffix}`);
  }

  const fullText = wordsParts.join(' e ');
  return fullText.charAt(0).toUpperCase() + fullText.slice(1);
};
